import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, supabaseUrl } from '@/lib/supabase';
import type { TenantPlan } from './admin.queries';

export interface BankAccount {
  id: string;
  bankName: string;
  accountType: string;
  accountNumber: string;
  holderName: string;
  holderDoc: string | null;
  notes: string | null;
  isActive: boolean;
  displayOrder: number;
}

export type PaymentProofStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SubscriptionPayment {
  id: string;
  tenantId: string;
  tenantName?: string;
  uploadedBy: string;
  plan: TenantPlan;
  months: number;
  amountCents: number;
  /** Módulos que el pago cubrió aparte del plan. */
  modules: string[];
  bankAccountId: string | null;
  bankName?: string;
  reference: string | null;
  paidAt: string | null;
  proofUrl: string | null;
  proofPath: string | null;
  status: PaymentProofStatus;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNotes: string | null;
  activatedUntil: string | null;
  createdAt: string;
}

export const subKeys = {
  all: ['subscription'] as const,
  banks: () => ['subscription', 'banks'] as const,
  myPayments: () => ['subscription', 'my-payments'] as const,
  pending: () => ['subscription', 'pending'] as const,
};

// ─── Bank accounts ─────────────────────────────────────────────────────
export function useBankAccounts() {
  return useQuery({
    queryKey: subKeys.banks(),
    queryFn: async (): Promise<BankAccount[]> => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .order('display_order')
        .order('bank_name');
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: r.id,
        bankName: r.bank_name,
        accountType: r.account_type,
        accountNumber: r.account_number,
        holderName: r.holder_name,
        holderDoc: r.holder_doc,
        notes: r.notes,
        isActive: r.is_active,
        displayOrder: r.display_order,
      }));
    },
  });
}

export function useUpsertBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<BankAccount> & { bankName: string; accountType: string; accountNumber: string; holderName: string }) => {
      const row = {
        ...(input.id ? { id: input.id } : {}),
        bank_name: input.bankName,
        account_type: input.accountType,
        account_number: input.accountNumber,
        holder_name: input.holderName,
        holder_doc: input.holderDoc ?? null,
        notes: input.notes ?? null,
        is_active: input.isActive ?? true,
        display_order: input.displayOrder ?? 0,
      };
      const { data, error } = input.id
        ? await supabase.from('bank_accounts').update(row).eq('id', input.id).select().single()
        : await supabase.from('bank_accounts').insert(row).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subKeys.banks() }),
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('bank_accounts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subKeys.banks() }),
  });
}

// ─── My payments (tenant) ───────────────────────────────────────────────
export function useMyPayments() {
  return useQuery({
    queryKey: subKeys.myPayments(),
    queryFn: async (): Promise<SubscriptionPayment[]> => {
      const { data, error } = await supabase
        .from('subscription_payments')
        .select('*, bank:bank_accounts(bank_name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map(rowToPayment);
    },
  });
}

export function useSubmitPaymentProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      plan: TenantPlan;
      months: number;
      /**
       * Los módulos que se pagan aparte del plan. Sin esto (undefined) la base
       * toma los que la empresa ya paga: es la renovación de lo que tiene.
       */
      modulos?: string[];
      bankAccountId: string;
      reference?: string;
      paidAt?: string;
      proofFile?: File;
    }) => {
      let proofUrl: string | null = null;
      let proofPath: string | null = null;

      if (input.proofFile) {
        // Get current tenant id from auth metadata
        const { data: sessionRes } = await supabase.auth.getSession();
        const tenantId = (sessionRes.session?.user.app_metadata as { tenant_id?: string } | null)?.tenant_id;
        if (!tenantId) throw new Error('NO_TENANT');

        const ext = input.proofFile.name.split('.').pop()?.toLowerCase() ?? 'jpg';
        const path = `${tenantId}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('payment-proofs')
          .upload(path, input.proofFile, { upsert: false });
        if (upErr) throw upErr;
        const { data: signed } = await supabase.storage.from('payment-proofs').createSignedUrl(path, 60 * 60 * 24 * 365);
        proofUrl = signed?.signedUrl ?? null;
        proofPath = path;
      }

      // Sin importe: lo calcula la base con la misma cuenta que se mostró.
      // Antes viajaba desde aquí y se guardaba tal cual llegaba.
      const { data, error } = await supabase.rpc('submit_payment_proof', {
        p_plan: input.plan,
        p_months: input.months,
        p_modulos: input.modulos ?? null,
        p_bank_account_id: input.bankAccountId,
        p_reference: input.reference ?? null,
        p_paid_at: input.paidAt ?? null,
        p_proof_url: proofUrl,
        p_proof_path: proofPath,
      });
      if (error) throw error;
      return data as { id: string; status: string; amountCents: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subKeys.myPayments() }),
  });
}

// ─── Super admin: pending approvals ─────────────────────────────────────
export function usePendingPayments() {
  return useQuery({
    queryKey: subKeys.pending(),
    queryFn: async (): Promise<SubscriptionPayment[]> => {
      const { data, error } = await supabase
        .from('subscription_payments')
        .select('*, tenant:tenants(name), bank:bank_accounts(bank_name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map(rowToPayment);
    },
  });
}

export function useApprovePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; notes?: string }) => {
      const { data, error } = await supabase.rpc('admin_approve_payment', {
        p_payment_id: input.id,
        p_notes: input.notes ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subKeys.all });
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useRejectPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; notes: string }) => {
      const { data, error } = await supabase.rpc('admin_reject_payment', {
        p_payment_id: input.id,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: subKeys.all }),
  });
}

// ─── helpers ───────────────────────────────────────────────────────────
type PaymentRow = {
  id: string; tenant_id: string; uploaded_by: string;
  plan: TenantPlan; months: number; amount_cents: number; modules: string[] | null;
  bank_account_id: string | null; reference: string | null; paid_at: string | null;
  proof_url: string | null; proof_path: string | null;
  status: PaymentProofStatus;
  reviewed_at: string | null; reviewed_by: string | null; review_notes: string | null;
  activated_until: string | null; created_at: string;
  tenant?: { name: string } | null;
  bank?: { bank_name: string } | null;
};
function rowToPayment(r: PaymentRow): SubscriptionPayment {
  return {
    id: r.id,
    tenantId: r.tenant_id,
    tenantName: r.tenant?.name,
    uploadedBy: r.uploaded_by,
    plan: r.plan,
    months: r.months,
    amountCents: r.amount_cents,
    modules: r.modules ?? [],
    bankAccountId: r.bank_account_id,
    bankName: r.bank?.bank_name,
    reference: r.reference,
    paidAt: r.paid_at,
    proofUrl: r.proof_url,
    proofPath: r.proof_path,
    status: r.status,
    reviewedAt: r.reviewed_at,
    reviewedBy: r.reviewed_by,
    reviewNotes: r.review_notes,
    activatedUntil: r.activated_until,
    createdAt: r.created_at,
  };
}


// ═══════════════════════════════════════════════════════════════════════════
// Pago en línea por pasarela (Mercado Pago)
// ═══════════════════════════════════════════════════════════════════════════

export interface GatewayConfigDto {
  provider: string;
  isEnabled: boolean;
  planPrices: Record<string, number>;
  sandbox: boolean;
}

/** Si la pasarela está activa, la pantalla ofrece pagar en línea. */
export function useGatewayConfig() {
  return useQuery({
    queryKey: ['subscription', 'gateway-config'],
    queryFn: async (): Promise<GatewayConfigDto | null> => {
      const { data, error } = await supabase
        .from('gateway_config')
        .select('provider,is_enabled,plan_prices,sandbox')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        provider: data.provider,
        isEnabled: data.is_enabled,
        planPrices: (data.plan_prices ?? {}) as Record<string, number>,
        sandbox: data.sandbox,
      };
    },
  });
}

export interface GatewayPaymentDto {
  id: string;
  plan: string;
  months: number;
  amountCents: number;
  status: string;
  statusDetail: string | null;
  paymentMethod: string | null;
  checkoutUrl: string | null;
  activatedUntil: string | null;
  paidAt: string | null;
  createdAt: string;
}

export function useGatewayPayments() {
  return useQuery({
    queryKey: ['subscription', 'gateway-payments'],
    queryFn: async (): Promise<GatewayPaymentDto[]> => {
      const { data, error } = await supabase.rpc('gateway_my_payments');
      if (error) throw error;
      return (data ?? []) as GatewayPaymentDto[];
    },
  });
}

/**
 * Pide el enlace de pago y lleva al checkout de Mercado Pago.
 *
 * El precio lo calcula la base, no el navegador: si viniera de aquí,
 * cualquiera podría pagar mil pesos por el plan más caro editando la
 * petición. La Edge Function existe porque crear la preferencia exige el
 * token privado de Mercado Pago, que no puede estar en el bundle.
 */
export function useStartGatewayCheckout() {
  return useMutation({
    mutationFn: async (input: { plan: string; months: number; modulos?: string[] }) => {
      const { data: sesion } = await supabase.auth.getSession();
      const token = sesion.session?.access_token;
      if (!token) throw new Error('Sesión expirada; vuelve a iniciar sesión');

      const resp = await fetch(`${supabaseUrl}/functions/v1/mercadopago-checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan: input.plan, months: input.months, modulos: input.modulos ?? null }),
      });

      const cuerpo = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = (cuerpo as { error?: string }).error ?? 'No se pudo generar el pago';
        throw new Error(
          msg.includes('GATEWAY_DISABLED')
            ? 'El pago en línea no está habilitado todavía'
            : msg.includes('FORBIDDEN')
            ? 'No tienes permiso para gestionar la suscripción'
            : msg,
        );
      }
      return cuerpo as { checkoutUrl: string; intentId: string };
    },
  });
}


// ═══════════════════════════════════════════════════════════════════════════
// Cobro por módulos (14/10/2026)
//
// El precio lo calcula UNA función de la base, `cotizar__`, y todo lo que
// muestra o cobra un precio la usa. Aquí no se suma ni se multiplica nada: se
// pide la cuenta y se pinta. Es la cuarta vez que un importe calculado en dos
// sitios se separa en este proyecto (ver Patrón — El precio calculado dos
// veces); por eso la pantalla no tiene ni una constante de precio.
// ═══════════════════════════════════════════════════════════════════════════

export interface LineaModulo { code: string; name: string; cents: number }

/** La cuenta de una suscripción, tal como la devuelve la base. */
export interface Cotizacion {
  plan: string;
  planName: string;
  planCents: number;
  planModules: string[];
  modulos: LineaModulo[];
  modulosCents: number;
  usuariosExtra: number;
  usuariosExtraCents: number;
  sedesExtra: number;
  sedesExtraCents: number;
  /** La empresa tiene un precio acordado para lo que ya tiene. */
  pactado: boolean;
  pactadoNota: string | null;
  mensualCents: number;
  meses: number;
  subtotalCents: number;
  descuentoCents: number;
  descuentoTasa: number;
  totalCents: number;
  /** Lo que se activa al pagar aparte del plan. */
  modulosExtra: string[];
}

export interface PlanDeCatalogo {
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  modules: string[];
  limits: Record<string, number>;
  esBase: boolean;
  annualDiscount: number;
  annualCents: number;
}

export interface ModuloDeCatalogo {
  code: string;
  name: string;
  description: string | null;
  isCore: boolean;
  /** Peluquería, VetCare u Hotel: todo plan lleva al menos uno. */
  esPrincipal: boolean;
  priceCents: number;
}

export interface CatalogoSuscripcion {
  planes: PlanDeCatalogo[];
  modulos: ModuloDeCatalogo[];
  tarifas: { usuarioExtraCents: number; sedeExtraCents: number };
}

/**
 * Precios de lista: planes a la venta, cada módulo y las tarifas sueltas.
 * Lo lee también el folleto sin sesión, así que no trae nada de ninguna
 * empresa.
 */
export function useCatalogoSuscripcion() {
  return useQuery({
    queryKey: ['subscription', 'catalogo'],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<CatalogoSuscripcion> => {
      const { data, error } = await supabase.rpc('catalogo_suscripcion');
      if (error) throw error;
      return data as CatalogoSuscripcion;
    },
  });
}

/**
 * Cuánto paga la empresa hoy (sin argumentos) o cuánto pagaría con otro plan
 * u otros módulos. `modulos` undefined = los que ya paga aparte.
 */
export function useCotizarMiSuscripcion(input: {
  plan?: string; modulos?: string[]; meses?: number; enabled?: boolean;
}) {
  const { plan, modulos, meses = 1, enabled = true } = input;
  return useQuery({
    queryKey: ['subscription', 'cotizacion', plan ?? null, modulos ? [...modulos].sort() : null, meses],
    enabled,
    // Una cotización vieja es un precio equivocado: se pide cada vez que cambia
    // la selección, y no se reintenta un rechazo de la base (plan retirado).
    staleTime: 0,
    queryFn: async (): Promise<Cotizacion> => {
      const { data, error } = await supabase.rpc('cotizar_mi_suscripcion', {
        p_plan: plan ?? null,
        p_modulos: modulos ?? null,
        p_meses: meses,
      });
      if (error) throw error;
      return data as Cotizacion;
    },
  });
}
